import * as Haptics from 'expo-haptics';
import Analytics from '@/utils/analytics';
import CheckmarkIcon from '@assets/icons/checkmark.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { Platform, StyleProp, TouchableOpacity, ViewStyle } from 'react-native';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';

interface Props {
  isMinimized: boolean;
  itemMetadata: ItemListOptionMetadata;
  style?: StyleProp<ViewStyle>;
}

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 16,
  top: 8,
};

export const DispositionButton: React.FC<Props> = ({
  isMinimized,
  itemMetadata,
  style,
}) => {
  const { updateDispositions } = useUpdateOrderGuideItemContext();
  const [styles, theme] = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  return (
    <TouchableOpacity
      accessibilityLabel={
        isMinimized ? i18n.t('expand_item') : i18n.t('minimize_item')
      }
      accessibilityRole="button"
      disabled={isLoading}
      focusable={false}
      hitSlop={HIT_SLOP}
      onPress={async () => {
        if (Platform.OS !== 'web') {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
        Analytics.track('disposition_button.press', {
          minimizing: !isMinimized,
          variantPackUUID: itemMetadata.variantPackUUID,
        });
        setIsLoading(true);
        await updateDispositions(itemMetadata, !isMinimized);
        setIsLoading(false);
      }}
      style={[
        styles.base,
        isMinimized ? styles.baseMinimized : styles.baseExpanded,
        style,
      ]}
      testID={`disposition-button-${isMinimized ? 'minimized' : 'expanded'}`}
    >
      <CheckmarkIcon
        height={16}
        stroke={isMinimized ? theme.colors.white : theme.colors.inactiveText}
        width={16}
      />
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    borderRadius: 13,
    cursor: 'pointer',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  baseExpanded: {
    backgroundColor: theme.colors.background,
  },
  baseMinimized: {
    backgroundColor: theme.colors.inactiveFill,
  },
}));
